// The seam between the admin's screens (SPEC-33, SPEC-34) and whatever
// answers them: who is signed in, the base, its export, one athlete's
// profile; since SPEC-44 her bilans presenciais and her app's language. The
// screens take it as a prop, so their tests run without Firebase.
//
// The server decides everything here. It checks the `team` claim on every
// call, logs every action, and stores only what the lib's rules accept.

import type {
  AdminBilanRequest,
  AdminBilanResponse,
  AdminCreateAccountRequest,
  AdminExportResponse,
  AdminLanguageResponse,
  AdminProfilePurpose,
  AdminProfileResponse,
  AdminUsersResponse,
  Language,
} from "../schema/types";

/** Who opened the admin: nobody, an account without the team claim, or the PYF team. */
export type AdminAccess = "signed-out" | "denied" | "team";

/** The server refused the call: the account is not, or is no longer, a team account. */
export class AdminDeniedError extends Error {
  override name = "AdminDeniedError";
}

/** The server knows no account by that id: a link to an account since deleted, or mistyped. */
export class AdminNotFoundError extends Error {
  override name = "AdminNotFoundError";
}

export interface AdminSource {
  /** Waits for the persisted session, then reads the claim from a fresh token. */
  access(): Promise<AdminAccess>;
  /** Opens a session; the email is trimmed and in lower case. Rejects with a SignInError. */
  signIn(email: string, password: string): Promise<void>;
  /** Ends the session on this browser, so another account can sign in. */
  signOut(): Promise<void>;
  /** The base, latest activity first. Rejects with an AdminDeniedError when the server refuses. */
  users(): Promise<AdminUsersResponse>;
  /** The whole base as a CSV file, whatever the screen filters. Rejects with an AdminDeniedError when the server refuses. */
  exportCsv(): Promise<AdminExportResponse>;
  /**
   * One athlete's profile and results. The server logs the read as what it
   * is for, before reading: « view » to show it, « export » for the file of
   * her answers. Rejects with an AdminDeniedError when the server refuses,
   * an AdminNotFoundError when the account does not exist.
   */
  profile(accountId: string, purpose: AdminProfilePurpose): Promise<AdminProfileResponse>;
  /**
   * One action on a bilan presencial (SPEC-45): create one, save a section
   * of it, lock or reopen it, or its file. Answers with the bilan as stored.
   * Rejects with an AdminDeniedError when the server refuses, an
   * AdminNotFoundError for an account or a bilan that does not exist.
   */
  bilan(request: AdminBilanRequest): Promise<AdminBilanResponse>;
  /** Sets her app's language (SPEC-52); the app reads it at its next opening. Rejects as profile does. */
  setLanguage(accountId: string, language: Language): Promise<AdminLanguageResponse>;
  /**
   * « + Nova utilizadora » (SPEC-54): creates her account with her Dados
   * gerais and language, then sends her the set-a-password email in that
   * language. `invited` is false when the account exists but the email
   * could not be sent. Rejects with an AdminDeniedError when the server
   * refuses, an AdminEmailTakenError when an account already has the email.
   */
  createAccount(request: AdminCreateAccountRequest): Promise<{ accountId: string; invited: boolean }>;
}

/** An account with this email already exists (SPEC-54): « Já existe uma conta com este email. » */
export class AdminEmailTakenError extends Error {
  override name = "AdminEmailTakenError";
}
